import { Group, Button, TextInput, NumberInput, Select, Switch, Loader, Space } from '@mantine/core';

import { paymentMethodModeList, paymentMethodModeTitles } from '../../utils/const/common';
import {
  isPaymentMethodWithCustomFlow,
  isPaymentMethodWithRemoteMethod,
} from '../../utils/helpers/payment-method-mode';
import { RemoteMethodFormFields } from './remote-method-form-fields';
import { CustomMethodFormFields } from './custom-method-form-fields';
import { CustomMethodFormOptions } from './custom-method-form-options';
import { ConversionPairFormField } from './conversion-pair-form-field';
import { PaymentVerificationFormFields } from './payment-verification-form-fields';

import { useCreatePaymentMethod } from '../../hooks/use-create-payment-method';

type Props = {
  onClose: () => void;
};

export const CreatePaymentMethodsForm = (props: Props) => {
  const { onClose } = props;

  const {
    avalableCurrencies,
    form,
    remoteMethods,
    conversionPairs,
    confirmationType,
    useBlockchain,
    dataOptions,
    methodMode,
    isRemoteMethodsLoading,
    isConversionPairsLoading,
    handleSubmit,
  } = useCreatePaymentMethod(onClose);

  if (isRemoteMethodsLoading || isConversionPairsLoading || !remoteMethods) {
    return <Loader color="blue" />;
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Ключ (key)"
        withAsterisk
        key={form.key('key')}
        {...form.getInputProps('key')}
      />
      <TextInput
        label="Название"
        withAsterisk
        key={form.key('name')}
        {...form.getInputProps('name')}
      />
      <NumberInput
        label="Процент пополнения"
        decimalScale={10}
        step={0.1}
        withAsterisk
        key={form.key('refillPercent')}
        {...form.getInputProps('refillPercent')}
      />
      <NumberInput
        label="Комиссия с клиента (%)"
        decimalScale={10}
        step={0.1}
        key={form.key('additionalPercent')}
        {...form.getInputProps('additionalPercent')}
      />
      <NumberInput
        label="Минимальная сумма пополнения"
        withAsterisk
        key={form.key('minRefill')}
        {...form.getInputProps('minRefill')}
      />
      <NumberInput
        label="Максимальная сумма пополнения"
        withAsterisk
        key={form.key('maxRefill')}
        {...form.getInputProps('maxRefill')}
      />
      <NumberInput
        label="Время действия заявки (минут)"
        min={1}
        allowDecimal={false}
        withAsterisk
        key={form.key('paymentExpireMinutes')}
        {...form.getInputProps('paymentExpireMinutes')}
      />
      <Select
        label="Валюта"
        withAsterisk
        data={avalableCurrencies.map((currency) => ({ value: currency, label: currency }))}
        key={form.key('currency')}
        {...form.getInputProps('currency')}
      />
      <Select
        label="Режим метода"
        withAsterisk
        data={paymentMethodModeList.map((mode) => ({ value: mode, label: paymentMethodModeTitles[mode] }))}
        key={form.key('mode')}
        {...form.getInputProps('mode')}
      />
      <ConversionPairFormField
        conversionPairs={conversionPairs}
        form={form}
      />

      {isPaymentMethodWithRemoteMethod(methodMode) && (
        <RemoteMethodFormFields
          remoteMethods={remoteMethods}
          form={form}
        />
      )}

      <Space h="md" />

      {isPaymentMethodWithCustomFlow(methodMode) && (
        <CustomMethodFormFields
          form={form}
          confirmationType={confirmationType}
          childrenOprions={<CustomMethodFormOptions form={form} items={dataOptions} />}
        />
      )}

      <PaymentVerificationFormFields
        form={form}
        showBlockchain={methodMode === 'custom' && form.getValues().currency === 'USDT'}
        useBlockchain={useBlockchain}
      />

      <Switch
        mt={10}
        label="Активен"
        key={form.key('active')}
        {...form.getInputProps('active', { type: 'checkbox' })}
      />

      <Group justify="flex-end" mt="30">
        <Button variant="outline" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button type="submit">
          Добавить
        </Button>
      </Group>
    </form>
  );
};
